import React from 'react';
import { Contact } from '../../types/contact';
import styles from './ContactTable.module.css';

interface ContactTableProps {
  contacts: Contact[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  onContactClick: (contact: Contact) => void;
}

interface ColumnDef {
  key: string;
  label: string;
  sortable: boolean;
  className?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'company', label: 'Company', sortable: true },
  { key: 'title', label: 'Title', sortable: false, className: 'colTitle' },
  { key: 'email', label: 'Email', sortable: false },
  { key: 'phone', label: 'Phone', sortable: false, className: 'colPhone' },
  { key: 'tags', label: 'Tags', sortable: false },
];

const MAX_VISIBLE_TAGS = 2;

export const ContactTable: React.FC<ContactTableProps> = ({
  contacts,
  sortBy,
  sortOrder,
  onSort,
  onContactClick,
}) => {
  if (contacts.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No contacts found</p>
      </div>
    );
  }

  const getSortIndicator = (columnKey: string) => {
    // Map column key to actual sort field
    const sortField = columnKey === 'name' ? 'firstName' : columnKey;
    const isActive = sortBy === sortField || (columnKey === 'name' && sortBy === 'lastName');
    if (!isActive) {
      return <span className={styles.sortIndicator}>↕</span>;
    }
    return (
      <span className={`${styles.sortIndicator} ${styles.active}`}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleSort = (columnKey: string) => {
    // Map 'name' column to 'lastName' for sorting
    const sortField = columnKey === 'name' ? 'lastName' : columnKey;
    onSort(sortField);
  };

  const getPrimaryEmail = (contact: Contact): string | null => {
    const primary = contact.emails.find(e => e.isPrimary);
    return (primary || contact.emails[0])?.email ?? null;
  };

  const getPrimaryPhone = (contact: Contact): string | null => {
    const primary = contact.phones.find(p => p.isPrimary);
    return (primary || contact.phones[0])?.phone ?? null;
  };

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                className={`${col.sortable ? styles.sortable : ''} ${col.className ? styles[col.className] : ''}`}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
              >
                {col.label}
                {col.sortable && getSortIndicator(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {contacts.map(contact => {
            const primaryEmail = getPrimaryEmail(contact);
            const primaryPhone = getPrimaryPhone(contact);
            const visibleTags = contact.tags.slice(0, MAX_VISIBLE_TAGS);
            const overflowCount = contact.tags.length - MAX_VISIBLE_TAGS;

            return (
              <tr key={contact.id} onClick={() => onContactClick(contact)}>
                <td>
                  <span className={styles.contactName}>
                    {contact.firstName} {contact.lastName}
                  </span>
                </td>
                <td>{contact.company || '—'}</td>
                <td className={styles.colTitle}>{contact.title || '—'}</td>
                <td>
                  {primaryEmail ? (
                    <span className={styles.emailText}>{primaryEmail}</span>
                  ) : '—'}
                </td>
                <td className={styles.colPhone}>
                  {primaryPhone ? (
                    <span className={styles.phoneText}>{primaryPhone}</span>
                  ) : '—'}
                </td>
                <td>
                  <div className={styles.tagsCell}>
                    {visibleTags.map(ct => (
                      <span key={ct.id} className={styles.tagPill}>
                        {ct.tag.name}
                      </span>
                    ))}
                    {overflowCount > 0 && (
                      <span className={styles.tagOverflow}>+{overflowCount}</span>
                    )}
                    {contact.tags.length === 0 && '—'}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ContactTable;
